import { useRef, useState } from 'react';

import { Button, Modal, Slider, Space } from 'antd';
import { useTranslation } from 'react-i18next';

import appI18n from '../../../config/i18n';
import { captureVideoFrame } from '../utils/captureVideoFrame';

interface Props {
  isOpen: boolean;
  videoUrl: string | null;
  onCancel: () => void;
  onPick: (blob: Blob, atSeconds: number) => void;
}

const formatTime = (s: number): string => {
  const total = Math.round(s || 0);
  const m = Math.floor(total / 60);
  const ss = String(total % 60).padStart(2, '0');
  return `${m}:${ss}`;
};

function FramePickerModal({ isOpen, videoUrl, onCancel, onPick }: Props) {
  const { t } = useTranslation(undefined, { i18n: appI18n });
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [capturing, setCapturing] = useState(false);

  const onLoadedMetadata = () => {
    const v = videoRef.current;
    if (v) setDuration(v.duration || 0);
  };

  const onSeek = (value: number) => {
    const v = videoRef.current;
    if (v) {
      v.currentTime = value;
      setCurrent(value);
    }
  };

  const handleUse = async () => {
    const v = videoRef.current;
    if (!v) return;
    setCapturing(true);
    try {
      // El seek de <video> es asíncrono: si todavía está buscando, esperar el
      // evento 'seeked' para no capturar el fotograma anterior al elegido.
      if (v.seeking) {
        await new Promise<void>((resolve) => {
          const onSeeked = () => {
            v.removeEventListener('seeked', onSeeked);
            resolve();
          };
          v.addEventListener('seeked', onSeeked);
        });
      }
      const blob = await captureVideoFrame(v);
      onPick(blob, v.currentTime);
    } finally {
      setCapturing(false);
    }
  };

  return (
    <Modal
      title={t('scenes.videos.scene.frame_picker.title')}
      open={isOpen}
      onCancel={onCancel}
      destroyOnClose
      width={640}
      maskClosable={false}
      footer={
        <Space>
          <Button onClick={onCancel}>
            {t('scenes.videos.scene.frame_picker.cancel')}
          </Button>
          <Button type="primary" loading={capturing} onClick={handleUse}>
            {t('scenes.videos.scene.frame_picker.use_frame')}
          </Button>
        </Space>
      }
    >
      {videoUrl && (
        <>
          {/*
            crossOrigin="anonymous" es OBLIGATORIO: en edición el video se
            carga desde la URL presignada de R2 (cross-origin). Sin esto, al
            dibujar el frame en el canvas este queda "tainted" y toBlob falla
            con SecurityError. R2 responde CORS para el origen del dashboard.
          */}
          <video
            ref={videoRef}
            src={videoUrl}
            crossOrigin="anonymous"
            muted
            playsInline
            preload="metadata"
            style={{
              display: 'block',
              margin: '0 auto',
              maxHeight: '50vh',
              maxWidth: '100%',
              width: 'auto',
              height: 'auto',
              borderRadius: 6,
              background: '#000',
            }}
            onLoadedMetadata={onLoadedMetadata}
          />
          <Slider
            min={0}
            max={duration}
            step={0.1}
            value={current}
            tooltip={{ formatter: (v) => formatTime(v ?? 0) }}
            onChange={onSeek}
            style={{ marginTop: 12 }}
          />
        </>
      )}
    </Modal>
  );
}

export default FramePickerModal;
